// client/src/components/pages/LexiStar/TripDetail/ReplanDialog.jsx
//
// Mid-trip re-plan (T4-S18; LS-09 §5.5; m3-contracts §7.3): a new destination
// and/or route, an optional via site, a reason code and a reason. The server
// (M3-A3) builds the new leg set from the truck's current position, pins the
// route version, adjusts the litre allowance (LS-04 §9), bumps plan_version
// and tells the engine; the trip report then shows both plans.
// POST /trips/:id:replan { to_location_id? | route_id?, via_location_id?, reason_code, reason }.
// The preview here is indicative (the chosen route's planned distance against
// the current loaded leg); the server's figures come back on confirm.

import React, { useEffect, useMemo, useState } from 'react';
import ThemedSelect from '../../../common/ThemedSelect';
import { listRoutes, listSites, pickList } from '../../../../services/lexistarStatusBoard';
import { replanTrip, pickObject } from '../../../../services/lexistarAssurance';
import { useReasonCodes, ReasonCodeSelect } from '../StatusBoard/atoms';
import { fmtKm, fmtL, fmtNum } from '../StatusBoard/format';
import {
  Modal,
  Field,
  ReasonField,
  InlineError,
  buttonClasses,
} from '../ReviewQueue/assuranceAtoms';
import { errorMessage } from '../ReviewQueue/assuranceShared';

export default function ReplanDialog({ open, trip, legs, onClose, onDone }) {
  const codes = useReasonCodes('deviation');
  const [mode, setMode] = useState('destination');
  const [toLocation, setToLocation] = useState('');
  const [routeId, setRouteId] = useState('');
  const [via, setVia] = useState('');
  const [reasonCode, setReasonCode] = useState('');
  const [reason, setReason] = useState('');
  const [sites, setSites] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    setMode('destination');
    setToLocation('');
    setRouteId('');
    setVia('');
    setReasonCode('');
    setReason('');
    setError(null);
    let alive = true;
    listSites()
      .then((r) => alive && setSites(pickList(r, 'locations')))
      .catch(() => alive && setSites([]));
    listRoutes()
      .then((r) => alive && setRoutes(pickList(r, 'routes').filter((x) => x.status !== 'retired')))
      .catch(() => alive && setRoutes([]));
    return () => {
      alive = false;
    };
  }, [open]);

  const loadedLeg = (legs || []).find((l) => l.kind === 'loaded');
  const route = useMemo(
    () => routes.find((r) => String(r.id) === String(routeId)),
    [routes, routeId]
  );
  const deltaKm =
    route?.planned_km != null && loadedLeg?.planned_km != null
      ? Number(route.planned_km) - Number(loadedLeg.planned_km)
      : null;

  const submit = async () => {
    if (mode === 'destination' && !toLocation) return setError('Choose the new destination.');
    if (mode === 'route' && !routeId) return setError('Choose the new route.');
    if (!reasonCode) return setError('Choose a reason code — every re-plan is audited with one.');
    if (!reason.trim()) return setError('Give the reason; it goes on the trip timeline.');
    setBusy(true);
    setError(null);
    try {
      const res = await replanTrip(trip.id, {
        to_location_id: mode === 'destination' ? Number(toLocation) : undefined,
        route_id: mode === 'route' ? routeId : undefined,
        via_location_id: via ? Number(via) : undefined,
        reason_code: reasonCode,
        reason: reason.trim(),
      });
      const t = pickObject(res, 'trip');
      onDone?.({
        planVersion: t?.plan_version ?? res?.plan_version ?? null,
        budget: res?.budget_adjusted_l ?? t?.budget_adjusted_l ?? null,
      });
    } catch (err) {
      setError(errorMessage(err, 'The trip was not re-planned.'));
    } finally {
      setBusy(false);
    }
  };

  const siteOptions = sites.map((s) => (
    <option key={s.location_id} value={String(s.location_id)}>
      {s.location_name}
    </option>
  ));

  return (
    <Modal
      open={open}
      title={`Re-plan ${trip?.reference || 'trip'}`}
      onClose={onClose}
      wide
      footer={
        <>
          <button type="button" onClick={onClose} className={buttonClasses.secondary}>
            Cancel
          </button>
          <button type="button" onClick={submit} disabled={busy} className={buttonClasses.primary}>
            {busy ? 'Re-planning…' : 'Re-plan the trip'}
          </button>
        </>
      }
    >
      <fieldset>
        <legend className="block text-sm font-medium text-slate-700 mb-1.5">What changes</legend>
        <div className="flex flex-wrap gap-2">
          {[
            ['destination', 'New destination'],
            ['route', 'New route'],
          ].map(([v, label]) => (
            <label
              key={v}
              className={`inline-flex items-center gap-2 px-3 min-h-[44px] rounded-lg border text-sm cursor-pointer ${mode === v ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-300 text-slate-700'}`}
            >
              <input
                type="radio"
                name="replan-mode"
                value={v}
                checked={mode === v}
                onChange={() => setMode(v)}
                className="sr-only"
              />
              {label}
            </label>
          ))}
        </div>
      </fieldset>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {mode === 'destination' ? (
          <Field label="New destination" htmlFor="replan-to">
            <ThemedSelect
              id="replan-to"
              value={toLocation}
              onChange={(e) => setToLocation(e.target.value)}
              aria-label="New destination"
            >
              <option value="">Choose a site…</option>
              {siteOptions}
            </ThemedSelect>
          </Field>
        ) : (
          <Field label="New route" htmlFor="replan-route">
            <ThemedSelect
              id="replan-route"
              value={routeId}
              onChange={(e) => setRouteId(e.target.value)}
              aria-label="New route"
            >
              <option value="">Choose a route…</option>
              {routes.map((r) => (
                <option key={r.id} value={String(r.id)}>
                  {r.code ? `${r.code} · ${r.name}` : r.name}
                  {r.planned_km != null ? ` (${fmtNum(r.planned_km)} km)` : ''}
                </option>
              ))}
            </ThemedSelect>
          </Field>
        )}
        <Field label="Via (optional)" htmlFor="replan-via">
          <ThemedSelect
            id="replan-via"
            value={via}
            onChange={(e) => setVia(e.target.value)}
            aria-label="Via"
          >
            <option value="">No via site</option>
            {siteOptions}
          </ThemedSelect>
        </Field>
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Reason code</label>
        <ReasonCodeSelect
          codes={codes}
          value={reasonCode}
          onChange={setReasonCode}
          label="Reason code"
        />
      </div>
      <ReasonField
        value={reason}
        onChange={setReason}
        placeholder="Customer diverted the load to Plant C"
      />
      <div
        className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-sm text-slate-700 space-y-0.5"
        aria-label="Re-plan preview"
      >
        <p>
          Current plan:{' '}
          {loadedLeg ? `${loadedLeg.from_name} → ${loadedLeg.to_name}` : 'no loaded leg'} ·{' '}
          {fmtKm(loadedLeg?.planned_km)} · allowance{' '}
          {fmtL(trip?.budget_adjusted_l ?? trip?.budget_original_l)}
        </p>
        {mode === 'route' && route && (
          <p className="tabular-nums">
            New route {route.code || route.name}: {fmtKm(route.planned_km)} planned
            {deltaKm != null &&
              ` (${deltaKm >= 0 ? '+' : '−'}${fmtNum(Math.abs(deltaKm))} km against the current leg)`}
          </p>
        )}
        <p className="text-xs text-slate-500">
          The server plans from the truck’s current position, pins the route version and adjusts the
          litre allowance; the driver is told through the notification rules. Both plans stay on the
          trip report.
        </p>
      </div>
      <InlineError message={error} />
    </Modal>
  );
}
